import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { getPreviewFiller } from '../../api/fillers/fillers';
import { formatLength } from '../../util/units';
/* ========== LOCALE ========== */
const locale = {
    ru: {
        loading: 'Загрузка...',
        error: 'Ошибка загрузки',
        empty: 'Нет доступных вариантов для этого размера',
        clear: 'Очистить слот',
        clearConfirm: 'Удалить текущий элемент?',
    },
    en: {
        loading: 'Loading...',
        error: 'Failed to load',
        empty: 'No options available for this size',
        clear: 'Clear slot',
        clearConfirm: 'Remove current element?',
    },
    de: {
        loading: 'Laden...',
        error: 'Fehler beim Laden',
        empty: 'Keine Optionen für diese Größe verfügbar',
        clear: 'Slot leeren',
        clearConfirm: 'Aktuelles Element entfernen?',
    },
};
/* ========== COMPONENT ========== */
export const FillerPicker = ({ width, height, depth, slotType, onSelect, fetchAvailable, onClose, units = 'ru', lang = 'ru', filled = false, onClear, }) => {
    const [groups, setGroups] = useState([]);
    const [state, setState] = useState('loading');
    const [activeId, setActiveId] = useState(null);
    const [confirmClear, setConfirmClear] = useState(false);
    const urlStatic = 'https://static.3dp.deallog.pro/';
    const t = locale[lang];
    useEffect(() => {
        setState('loading');
        fetchAvailable({ width, height, depth, slotType })
            .then(data => {
            setGroups(data);
            setState('ready');
        })
            .catch(() => setState('error'));
    }, [width, height]);
    const handleSelect = async (id, item) => {
        setActiveId(id);
        setState('calculating');
        try {
            await onSelect(id, item);
            setState('ready');
        }
        catch {
            setState('error');
            setActiveId(null);
        }
    };
    const handleClear = async () => {
        if (!confirmClear) {
            setConfirmClear(true);
            return;
        }
        setConfirmClear(false);
        onClear?.();
    };
    const isMobile = window.innerWidth < 768;
    const panelStyle = isMobile ? {
        position: 'fixed', bottom: 0, left: 0, right: 0,
        maxHeight: '70vh', borderRadius: '16px 16px 0 0',
        boxShadow: '0 -8px 32px rgba(0,0,0,0.18)',
        background: '#fff', zIndex: 1000, overflowY: 'auto', padding: 16,
    } : {
        position: 'fixed', left: 0, top: 0, bottom: 0,
        width: '50vw',
        boxShadow: '8px 0 32px rgba(0,0,0,0.15)',
        background: '#fff', zIndex: 1000, overflowY: 'auto', padding: 24,
    };
    return (_jsxs(_Fragment, { children: [isMobile && (_jsx("div", { onClick: onClose, style: {
                    position: 'fixed', inset: 0,
                    background: 'rgba(0,0,0,0.4)', zIndex: 999,
                } })), _jsxs("div", { style: panelStyle, children: [_jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }, children: [_jsxs("span", { style: { fontWeight: 600, fontSize: 15 }, children: [formatLength(width, units), " \u00D7 ", formatLength(height, units)] }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 8 }, children: [filled && onClear && (_jsx("button", { onClick: handleClear, style: {
                                            display: 'flex', alignItems: 'center', gap: 5,
                                            padding: '5px 10px', borderRadius: 7,
                                            border: confirmClear
                                                ? '1.5px solid #e53e3e'
                                                : '1.5px solid #e2e8f0',
                                            background: confirmClear ? '#fff5f5' : '#f7fafc',
                                            color: confirmClear ? '#e53e3e' : '#718096',
                                            fontSize: 12, fontWeight: 600,
                                            cursor: 'pointer', whiteSpace: 'nowrap',
                                            transition: 'all 0.15s',
                                        }, children: confirmClear ? `${t.clearConfirm}` : `✕ ${t.clear}` })), _jsx("button", { onClick: onClose, style: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#718096' }, children: "\u2715" })] })] }), state === 'loading' && (_jsx("div", { style: { textAlign: 'center', color: '#a0aec0', padding: '24px 0' }, children: t.loading })), state === 'error' && (_jsx("div", { style: { color: '#e53e3e', textAlign: 'center', padding: '24px 0' }, children: t.error })), state === 'ready' && groups.length === 0 && (_jsx("div", { style: { textAlign: 'center', color: '#a0aec0', padding: '24px 0' }, children: t.empty })), (groups ?? []).map(group => (_jsxs("div", { style: { marginBottom: 16 }, children: [_jsx("div", { style: {
                                    fontSize: 11, fontWeight: 700, color: '#a0aec0',
                                    textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8,
                                }, children: group.name || 'No Group' }), _jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: 8 }, children: (group.items ?? []).map(item => {
                                    const isActive = activeId === item.id;
                                    const isCalculating = state === 'calculating' && isActive;
                                    return (_jsxs("div", { onClick: () => !isCalculating && handleSelect(item.id, item), style: {
                                            display: 'flex', alignItems: 'center', gap: 10,
                                            padding: '8px 10px', borderRadius: 8,
                                            border: `1.5px solid ${isActive ? '#4488ff' : '#e2e8f0'}`,
                                            background: isActive ? '#ebf4ff' : '#fff',
                                            cursor: isCalculating ? 'wait' : 'pointer',
                                        }, children: [item.preview_url && (_jsx("img", { src: urlStatic + getPreviewFiller(item, 'tb'), alt: item.name, style: { width: 200, height: 100, objectFit: 'contain', borderRadius: 6, flexShrink: 0 } })), _jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [_jsx("div", { style: { fontWeight: 600, fontSize: 13, color: '#1a202c' }, children: item.name }), item.description && (_jsx("div", { style: { fontSize: 11, color: '#718096', marginTop: 2 }, children: item.description }))] }), isCalculating
                                                ? _jsx("div", { style: { width: 16, height: 16, border: '2px solid #e2e8f0', borderTop: '2px solid #4488ff', borderRadius: '50%' } })
                                                : isActive
                                                    ? _jsx("span", { style: { color: '#4488ff' }, children: "\u2713" })
                                                    : _jsx("span", { style: { color: '#cbd5e0' }, children: "\u203A" })] }, item.id));
                                }) })] }, group.id ?? 'none')))] })] }));
};
